import React, { createContext, useContext } from 'react';

/**
 * @typedef {Object} Auth
 */

/**
 * AuthContext is a React Context that provides a mechanism to share
 * authentication data throughout the component tree without needing to pass
 * props down manually at every level.
 *
 * This context is intended to be used by components that require access to
 * authentication state or need to perform authentication-related operations.
 *
 * @type {React.Context<{ auth: Auth | null }>}
 */
export const AuthContext = createContext({
    auth: null
});

/**
 * AuthProvider is a React component that wraps its children with an AuthContext.Provider.
 * It provides an authentication context to all descendant components, making the `auth` object accessible throughout the component tree.
 *
 * @param {Object} props - The props for the AuthProvider component.
 * @param {React.ReactNode} props.children - The child components that will be wrapped with the AuthContext provider.
 * @param {Object} props.auth - The authentication object to be provided to the AuthContext.
 *
 * @returns {JSX.Element} A React component that provides the authentication context to its children.
 */
export const AuthProvider = ({ children, auth }) => {
    return <AuthContext.Provider value={{ auth }}>{children}</AuthContext.Provider>;
};

/**
 * A custom hook that provides access to the authentication context.
 *
 * @returns {Auth} The value of the AuthContext, typically representing the
 * authentication state and associated actions.
 *
 * @throws {Error} If used outside of an AuthContext provider.
 */
export const useAuth = () => {
    const { auth } = useContext(AuthContext);

    if (!auth) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return auth;
};
